import React, { useState, createContext, ReactNode, useEffect } from "react";
import { getAuth } from "firebase/auth";
import {
  getUserAccountingIntegration,
  updateUserQbTaxGroup,
  takePrefetchedAccountingIntegration,
} from "@/app/_firebase/user";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import { useUserContext } from "@/app/_hooks/useUserContext";
import AnimatedLoader from "@/app/_components/AnimatedLoader/AnimatedLoader";
import QuickBooksLogo from "@/app/_media/quickbooks.svg";
import NextImage from "next/image";
import {
  Alert,
  Anchor,
  Modal,
  Portal,
  Title,
  Text,
  Stack,
  Group,
  Image,
  Button,
  List,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

import useIntegrateQuickbooks from "@/app/_hooks/quickbooks/useIntegrateQuickbooks";
import { AccountingIntegration } from "@/app/@types/enums";

import { QBBankAccount, QBTaxGroup } from "@/app/@types/quickbooks";
import QuickbooksSetup from "./components/QuickbooksSetup/QuickbooksSetup";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";

type AccountingContextType = {
  type: AccountingIntegration;
  company: any;
} | null;

interface AccountingContextProps {
  accountingContext: AccountingContextType;
  setAccountingContext: (value: AccountingContextType) => void;
}

const defaultAlertContext: AccountingContextType = null;

export const AccountingContext = createContext<AccountingContextProps>({
  accountingContext: defaultAlertContext,
  setAccountingContext: () => {},
});

interface AccountingProviderProps {
  children: ReactNode;
}

export const AccountingProvider = ({ children }: AccountingProviderProps) => {
  const [quickbooksParam, setQuickbooksParam] = useState<string | null>(null);
  const user = useUserContext();

  const setAlert = useSetAlert();
  const [accountingContext, setAccountingContext] =
    useState<AccountingContextType>(defaultAlertContext);
  const [showReauthenticateModal, setShowReauthenticateModal] = useState<
    false | { type: string }
  >(false);

  const [showQuickbooksSetupModal, setShowQuickbooksSetupModal] =
    useState(false);
  const [availableTaxGroups, setAvailableTaxGroups] = useState<QBTaxGroup[]>(
    []
  );
  const [availableBankAccounts, setAvailableBankAccounts] = useState<
    QBBankAccount[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accountingError, setAccountingError] = useState(false);
  // Live status of Intuit's own platform, from their public status page
  // (Statuspage JSON, permissive CORS). Lets the banner say "QuickBooks is
  // down, it's them not us" during an Intuit outage instead of a generic
  // error, and warn even when our own settings loaded fine.
  const [qbPlatformStatus, setQbPlatformStatus] = useState<{
    indicator: string;
    description: string;
  } | null>(null);
  const [qbOutageDismissed, setQbOutageDismissed] = useState(false);
  const firstRender = React.useRef(true);

  const {
    integrateQuickbooks,
    isLoading: isIntegratingQuickbooks,
    data: quickBooksIntegrationData,
  } = useIntegrateQuickbooks();

  useEffect(() => {
    if (!firstRender.current) return;
    if (!user?.uid) return;
    if (!isLoading) return;

    const searchParams = new URLSearchParams(window.location.search);
    const quickbooksParam = searchParams.get("quickbooks");
    if (quickbooksParam) {
      setShowQuickbooksSetupModal(true);
    }
  }, [user, isLoading]);

  // The Firestore read can fail transiently right after a re-login while the
  // fresh auth token propagates (surfaces as "Missing or insufficient
  // permissions"). Retry with a forced token refresh before giving up.
  const getAccountingIntegrationWithRetry = async (uid: string) => {
    const MAX_ATTEMPTS = 3;
    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        // First attempt reuses the read PermissionsProvider already started.
        const prefetched =
          attempt === 1 ? takePrefetchedAccountingIntegration(uid) : undefined;
        return await (prefetched ?? getUserAccountingIntegration(uid));
      } catch (error) {
        lastError = error;
        if (attempt < MAX_ATTEMPTS) {
          await getAuth()
            .currentUser?.getIdToken(true)
            .catch(() => {});
          await new Promise((resolve) => setTimeout(resolve, attempt * 1500));
        }
      }
    }
    throw lastError;
  };

  const fetchData = async () => {
    // 1: fetchData started
    setIsLoading(true);
    setAccountingError(false);

    try {
      // 2: Getting user accounting integration
      const accountingMethod = await getAccountingIntegrationWithRetry(
        user.uid
      );

      if (accountingMethod === AccountingIntegration.quickbooks) {
        // 3: QuickBooks integration detected
        const companyResponse = await authenticatedFetch(
          `/api/quickbooks/company/get`
        );

        // 4: Fetched company data
        const companyData = await companyResponse.json();

        if (companyData.error) {
          console.log({ companyData: companyData.error });
          // 5: Company data error detected
          if (companyData.error === "reauthenticate") {
            // 6: Reauthentication required
            setShowReauthenticateModal({ type: "quickbooks" });
          } else {
            // 7: General QuickBooks error: degrade to native and raise the
            // banner, whose Retry re-runs this load.
            setAccountingError(true);
          }
          setAccountingContext({
            type: AccountingIntegration.native,
            company: null,
          });
          return;
        }

        // 8: Fetching tax setup data
        const taxSetupResponse = await authenticatedFetch(
          `/api/quickbooks/tax/setup`
        );

        const taxSetupData = await taxSetupResponse.json();

        if (taxSetupData.error) {
          // 9: Tax setup data error: same as the company error above.
          setAccountingError(true);

          setAccountingContext({
            type: AccountingIntegration.native,
            company: null,
          });
          return;
        }

        setAvailableTaxGroups(taxSetupData.data.taxGroups);
        setAvailableBankAccounts(taxSetupData.data.bankAccounts);
        if (!user.settings.qbTaxGroup) {
          setShowQuickbooksSetupModal(true);
        } else {
          // 12: Checking for tax rate changes
          const matchingTaxGroup: QBTaxGroup | undefined =
            taxSetupData.data.taxGroups.find(
              (taxGroup: QBTaxGroup) =>
                taxGroup.taxGroupId === user.settings.qbTaxGroup.taxGroupId
            );
          if (
            matchingTaxGroup &&
            user.settings.qbTaxGroup.totalRate !== matchingTaxGroup.totalRate
          ) {
            // 13: Updating user tax group
            await updateUserQbTaxGroup(user.uid, matchingTaxGroup);

            setAlert({
              type: "info",
              content:
                "We detected your tax rates have changed on QuickBooks. We've updated your tax settings.",
              title: "Tax Settings Updated",
            });
          }
        }

        // 14: Setting QuickBooks accounting context
        setAccountingContext({
          type: AccountingIntegration.quickbooks,
          company: companyData.data,
        });
      } else {
        // 15: Setting native accounting context
        setAccountingContext({
          type: AccountingIntegration.native,
          company: null,
        });
      }
    } catch (error: any) {
      // 16: Caught an error. Leave the context null so QuickBooks users are
      // never silently treated as native; the banner below offers a retry.
      setAccountingError(true);
    } finally {
      // 17: Finishing up
      firstRender.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    if (!firstRender.current) return;

    fetchData();
  }, [user]);

  const isQuickbooksTenant =
    accountingContext?.type === AccountingIntegration.quickbooks;

  useEffect(() => {
    if (!isQuickbooksTenant && !accountingError) return;

    let cancelled = false;
    const checkIntuitStatus = async () => {
      try {
        const res = await fetch(
          "https://status.developer.intuit.com/api/v2/summary.json"
        );
        const data = await res.json();
        // Only the components that break MountMonitor's QuickBooks features:
        // the Online API itself and the OAuth service that signs our calls.
        // Payments API, Sandbox, Apps.com and portal outages are ignored.
        // Component ids are Statuspage-stable. The page-wide indicator is
        // useless here: Intuit ran a "major outage" incident on 2 Sep 2026
        // without ever flipping it.
        const WATCHED = new Set([
          "qgdpdf6lzwjx", // QuickBooks Online API
          "j7cwvgdn7cys", // OAuth APIs
        ]);
        const down = (data?.components || []).filter(
          (c: { id: string; status: string }) =>
            WATCHED.has(c.id) && c.status !== "operational"
        );
        if (!cancelled) {
          setQbPlatformStatus(
            down.length
              ? {
                  indicator: "outage",
                  description: down
                    .map(
                      (c: { name: string; status: string }) =>
                        `${c.name}: ${c.status.replace(/_/g, " ")}`
                    )
                    .join(", "),
                }
              : { indicator: "none", description: "" }
          );
        }
      } catch {
        // Their status page being unreachable tells us nothing; stay quiet.
      }
    };

    checkIntuitStatus();
    const interval = setInterval(checkIntuitStatus, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isQuickbooksTenant, accountingError]);

  const qbOutage =
    qbPlatformStatus && qbPlatformStatus.indicator !== "none"
      ? qbPlatformStatus
      : null;

  useEffect(() => {
    if (quickBooksIntegrationData?.data) {
      window.location.href = quickBooksIntegrationData?.data;
    }
  }, [quickBooksIntegrationData]);

  return (
    <AccountingContext.Provider
      value={{ accountingContext, setAccountingContext }}
    >
      {isLoading ? (
        <AnimatedLoader bkgOpacity={1} />
      ) : (
        <>
          <Portal>
            <Modal.Root
              size="content"
              centered
              opened={!!showReauthenticateModal}
              onClose={() => {}}
              zIndex={1000000000}
            >
              <Modal.Overlay />
              <Modal.Content>
                <Modal.Header>
                  <Group>
                    <Image
                      src={QuickBooksLogo}
                      component={NextImage}
                      h={48}
                      w={48}
                      alt="QuickBooks Logo"
                    />
                    <Title>Reconnect QuickBooks</Title>
                  </Group>
                </Modal.Header>
                <Modal.Body p="xl">
                  <Stack gap="md">
                    <Text>Your QuickBooks session has expired.</Text>
                    <Text>
                      Please reconnect to continue using QuickBooks features.
                    </Text>
                    <Text>
                      If you do not reconnect now you will not sync any further
                      data with QuickBooks.
                    </Text>
                    <Group mt="xl" justify="flex-end">
                      <Button
                        size="md"
                        onClick={integrateQuickbooks}
                        loading={isIntegratingQuickbooks}
                      >
                        Reconnect QuickBooks
                      </Button>
                    </Group>
                  </Stack>
                </Modal.Body>
              </Modal.Content>
            </Modal.Root>

            <QuickbooksSetup
              taxGroups={availableTaxGroups}
              bankAccounts={availableBankAccounts}
              isOpen={showQuickbooksSetupModal}
              onClose={() => setShowQuickbooksSetupModal(false)}
            />

            {accountingError && (
              <Alert
                icon={<IconAlertTriangle size={18} />}
                color="orange"
                title="Accounting settings unavailable"
                style={{
                  position: "fixed",
                  top: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10000,
                  maxWidth: 480,
                  boxShadow: "var(--mantine-shadow-md)",
                  // Solid background: the default light variant is
                  // translucent and unreadable over page content.
                  backgroundColor: "var(--mantine-color-orange-0)",
                }}
              >
                <Stack gap="xs">
                  <Text size="sm">
                    {qbOutage
                      ? `QuickBooks is having an outage on their end right now (${qbOutage.description}, per Intuit's status page), so QuickBooks features are paused. Your MountMonitor jobs and payments are unaffected.`
                      : "We could not load your accounting settings, so features like QuickBooks syncing are paused for now."}
                  </Text>
                  {qbOutage && (
                    <Anchor
                      size="sm"
                      href="https://status.developer.intuit.com/"
                      target="_blank"
                      rel="noopener"
                    >
                      Check QuickBooks status
                    </Anchor>
                  )}
                  <Group justify="flex-end">
                    <Button size="xs" variant="light" onClick={fetchData}>
                      Retry
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {!accountingError && qbOutage && !qbOutageDismissed && (
              <Alert
                icon={<IconAlertTriangle size={18} />}
                color="orange"
                title="QuickBooks is having issues"
                withCloseButton
                onClose={() => setQbOutageDismissed(true)}
                style={{
                  position: "fixed",
                  top: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10000,
                  maxWidth: 480,
                  boxShadow: "var(--mantine-shadow-md)",
                  // Solid background: the default light variant is
                  // translucent and unreadable over page content.
                  backgroundColor: "var(--mantine-color-orange-0)",
                }}
              >
                <Stack gap="xs">
                  <Text size="sm">
                    Intuit is reporting problems with QuickBooks right now
                    ({qbOutage.description}). Invoice and payment syncing may
                    fail until they fix it. Your MountMonitor jobs and payments
                    are unaffected.
                  </Text>
                  <Anchor
                    size="sm"
                    href="https://status.developer.intuit.com/"
                    target="_blank"
                    rel="noopener"
                  >
                    Check QuickBooks status
                  </Anchor>
                </Stack>
              </Alert>
            )}
          </Portal>
          {children}
        </>
      )}
    </AccountingContext.Provider>
  );
};
